/**
 * Cómo entra al carrito un código que resolvió el servidor.
 *
 * Pasa con lo que la copia del catálogo en el teléfono todavía no tiene: un
 * producto recién creado, el código de una talla, la etiqueta de la balanza.
 * Ver `procesarCodigo` en `PosPage`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LOS DOS FALLOS QUE TAPA ESTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · **El mismo producto en dos líneas.** Se agregaba con el id de su
 *     variante, y al tocarlo o encontrarlo en la copia local entraba sin
 *     variante: dos claves distintas para lo mismo, y no se sumaba.
 *   · **La balanza que borraba.** Se agregaba uno y después se ponía la línea
 *     entera al peso de la etiqueta: dos cortes del mismo queso —0,4 kg y
 *     0,3 kg— dejaban 0,3 kg. Ahora el peso entra sumado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y LA BALANZA QUE IMPRIME PRECIO, QUE SE COBRABA COMO UN KILO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hay balanzas que meten en la etiqueta el PRECIO del corte en vez del peso.
 * El servidor lo leía bien (`importe`), pero aquí solo se miraba el peso: sin
 * peso, el corte entraba como UN KILO al precio del catálogo. Un queso de
 * 3,40 $ se cobraba 10 $.
 *
 * Ahora el importe se pasa a cantidad con la misma cuenta de «dame dos
 * dólares de queso» (`cantidadDesdeDinero`, con el impuesto dentro). Así se
 * cobra LO QUE DICE LA ETIQUETA, que es lo que el cliente tiene impreso, aunque
 * el kilo haya cambiado desde que se imprimió: importe ÷ precio de hoy × precio
 * de hoy = importe. Lo único que puede salir distinto es el peso que se
 * descuenta del inventario si el kilo cambió entre medias, y eso es mucho
 * menos grave que cobrar otra cosa. Como la cantidad va al gramo, el total
 * puede bailar un céntimo.
 */

import type { ItemListRow, Variant } from "@/lib/types";
import { cantidadDesdeDinero, precioFinalPorUnidad } from "./pesoYDinero";

export function comoEntraLoEscaneado(r: {
  item: Pick<ItemListRow, "has_variants"> &
    Partial<Pick<ItemListRow, "base_price" | "tax_rate" | "tax_inclusive">>;
  variant: Pick<Variant, "id" | "name">;
  de_balanza: boolean;
  cantidad: string | null;
  importe?: string | null;
}): { variantId: string | null; varianteNombre: string | null; cantidad: number | undefined } {
  // Sin tallas, sin variante: igual que al tocarlo o al encontrarlo en la copia.
  const conTalla = r.item.has_variants;
  let peso = r.de_balanza && r.cantidad ? Number(r.cantidad) : NaN;
  if (r.de_balanza && !(peso > 0) && r.importe) {
    // La etiqueta trae el precio: se pasa a cantidad. Ver el encabezado.
    peso = cantidadDesdeDinero(
      Number.parseFloat(r.importe),
      precioFinalPorUnidad(
        Number.parseFloat(r.item.base_price ?? "0") || 0,
        Number.parseFloat(r.item.tax_rate ?? "0") || 0,
        r.item.tax_inclusive ?? true,
      ),
    );
  }
  return {
    variantId: conTalla ? r.variant.id : null,
    varianteNombre: conTalla ? (r.variant.name ?? null) : null,
    cantidad: peso > 0 ? peso : undefined,
  };
}
